import { createApi } from "@reduxjs/toolkit/query/react";
import toast from "react-hot-toast";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import {
  API_COMPANY_DATA_TRANSFER_CANCEL,
  API_COMPANY_DATA_TRANSFER_ENTITIES,
  API_COMPANY_DATA_TRANSFER_ERRORS,
  API_COMPANY_DATA_TRANSFER_EXPORT,
  API_COMPANY_DATA_TRANSFER_EXPORTS,
  API_COMPANY_DATA_TRANSFER_IMPORT,
  API_COMPANY_DATA_TRANSFER_IMPORTS,
  API_COMPANY_DATA_TRANSFER_MAPPING,
  API_COMPANY_DATA_TRANSFER_RUN,
  API_COMPANY_DATA_TRANSFER_UNDO,
  API_COMPANY_DATA_TRANSFER_VALIDATE,
} from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type {
  ICancelRunResponse,
  ICreateExportPayload,
  ICreateExportResponse,
  IEntitiesManifest,
  IListRowErrorsParams,
  IListRowErrorsResponse,
  IListRunsParams,
  IListRunsResponse,
  IRunImportPayload,
  IRunWithView,
  ISubmitMappingPayload,
  IUndoImportPayload,
  IUndoRunResponse,
} from "@/types/(company)/data-transfer.interface";

export const companyDataTransferApi = createApi({
  reducerPath: "companyDataTransferApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["TransferRuns", "TransferRun", "TransferErrors", "TransferEntities"],
  endpoints: (builder) => ({
    /**
     * Registry manifest. Cached indefinitely — it only changes when the server's
     * descriptor registry does, and the version stamp on it tells us when.
     */
    getTransferEntities: builder.query<IApiResponse<IEntitiesManifest>, void>({
      query: () => ({ url: API_COMPANY_DATA_TRANSFER_ENTITIES, method: "GET" }),
      providesTags: [{ type: "TransferEntities", id: "MANIFEST" }],
    }),

    listTransferRuns: builder.query<IListRunsResponse, IListRunsParams>({
      query: (params) => ({
        url: API_COMPANY_DATA_TRANSFER_IMPORTS,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((run) => ({
                type: "TransferRun" as const,
                id: run.id,
              })),
              { type: "TransferRuns" as const, id: "LIST" },
            ]
          : [{ type: "TransferRuns" as const, id: "LIST" }],
    }),

    /**
     * The poll target.
     *
     * Provides ONLY its own id — deliberately not the LIST tag. The history screen
     * and a running job are mounted together, so a broad tag here would refetch the
     * entire history list on every 2-second poll.
     */
    getTransferRun: builder.query<
      IApiResponse<IRunWithView>,
      { runId: number; sheet?: string }
    >({
      query: ({ runId, sheet }) => ({
        url: API_COMPANY_DATA_TRANSFER_IMPORT(runId),
        method: "GET",
        params: sheet ? { sheet } : undefined,
      }),
      providesTags: (_r, _e, { runId }) => [{ type: "TransferRun", id: runId }],
    }),

    /**
     * Upload + analyze. `FormData` is passed straight through as the body with no
     * Content-Type header, so the browser sets the multipart boundary itself.
     *
     * No toast: analysing a large workbook can take a while, and the upload card's
     * own inline state is the right affordance — a hanging toast is not.
     */
    analyzeTransferWorkbook: builder.mutation<
      IApiResponse<IRunWithView>,
      { body: FormData }
    >({
      query: ({ body }) => ({
        url: API_COMPANY_DATA_TRANSFER_IMPORTS,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "TransferRuns", id: "LIST" }],
    }),

    /**
     * Mapping autosave.
     *
     * The server returns the RECOMPUTED view, which is written straight into the
     * cache rather than invalidated: an invalidation would thrash the poll and
     * could stomp an edit the operator has in flight.
     */
    updateTransferMapping: builder.mutation<
      IApiResponse<IRunWithView>,
      ISubmitMappingPayload
    >({
      query: ({ runId, ...body }) => ({
        url: API_COMPANY_DATA_TRANSFER_MAPPING(runId),
        method: "PATCH",
        body,
      }),
      async onQueryStarted({ runId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            companyDataTransferApi.util.updateQueryData(
              "getTransferRun",
              { runId },
              (draft) => {
                if (data?.data) draft.data = data.data;
              },
            ),
          );
        } catch {
          // The next poll or a retry surfaces it; a failed autosave must not
          // interrupt the operator mid-edit with a toast.
        }
      },
    }),

    validateTransferRun: builder.mutation<
      IApiResponse<IRunWithView>,
      { runId: number }
    >({
      query: ({ runId }) => ({
        url: API_COMPANY_DATA_TRANSFER_VALIDATE(runId),
        method: "POST",
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        const toastId = toast.loading("Checking your file…");
        try {
          await queryFulfilled;
          toast.success("Checking started", { id: toastId });
        } catch (error) {
          toast.error(
            (error as { error?: { data?: { message?: string } } })?.error?.data
              ?.message ?? "Could not start the check",
            { id: toastId },
          );
        }
      },
      invalidatesTags: (_r, _e, { runId }) => [{ type: "TransferRun", id: runId }],
    }),

    runTransferImport: builder.mutation<
      IApiResponse<IRunWithView>,
      IRunImportPayload
    >({
      query: ({ runId, ...body }) => ({
        url: API_COMPANY_DATA_TRANSFER_RUN(runId),
        method: "POST",
        body,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        const toastId = toast.loading("Starting the import…");
        try {
          await queryFulfilled;
          toast.success("Import started", { id: toastId });
        } catch (error) {
          // The server's message carries the actionable detail — a seat shortfall
          // spells out the arithmetic — so it is shown verbatim.
          toast.error(
            (error as { error?: { data?: { message?: string } } })?.error?.data
              ?.message ?? "Could not start the import",
            { id: toastId, duration: 8000 },
          );
        }
      },
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "TransferRun", id: runId },
        { type: "TransferRuns", id: "LIST" },
      ],
    }),

    cancelTransferRun: builder.mutation<
      IApiResponse<ICancelRunResponse>,
      { runId: number }
    >({
      query: ({ runId }) => ({
        url: API_COMPANY_DATA_TRANSFER_CANCEL(runId),
        method: "POST",
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        const toastId = toast.loading("Cancelling…");
        try {
          const { data } = await queryFulfilled;
          // The server says whether anything had already been written, which is
          // the part the operator actually needs to know.
          toast.success(data?.message ?? "Cancelled", {
            id: toastId,
            duration: 7000,
          });
        } catch (error) {
          toast.error(
            (error as { error?: { data?: { message?: string } } })?.error?.data
              ?.message ?? "Could not cancel the run",
            { id: toastId },
          );
        }
      },
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "TransferRun", id: runId },
        { type: "TransferRuns", id: "LIST" },
      ],
    }),

    undoTransferRun: builder.mutation<
      IApiResponse<IUndoRunResponse>,
      IUndoImportPayload
    >({
      query: ({ runId, ...body }) => ({
        url: API_COMPANY_DATA_TRANSFER_UNDO(runId),
        method: "POST",
        body,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        const toastId = toast.loading("Undoing the import…");
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message ?? "Undo started", { id: toastId });
        } catch (error) {
          // A blocked undo names the records standing in the way, so it needs
          // longer on screen than a generic failure.
          toast.error(
            (error as { error?: { data?: { message?: string } } })?.error?.data
              ?.message ?? "Could not undo this import",
            { id: toastId, duration: 10000 },
          );
        }
      },
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "TransferRun", id: runId },
        { type: "TransferRuns", id: "LIST" },
      ],
    }),

    /**
     * Starts an export. Always asynchronous — a full-scope export of a large
     * company runs for minutes, so the row appears immediately and polls.
     */
    createTransferExport: builder.mutation<
      ICreateExportResponse,
      ICreateExportPayload
    >({
      query: (body) => ({
        url: API_COMPANY_DATA_TRANSFER_EXPORTS,
        method: "POST",
        body,
      }),
      async onQueryStarted(_args, { queryFulfilled }) {
        const toastId = toast.loading("Starting your export…");
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message ?? "Export started", { id: toastId });
        } catch (error) {
          toast.error(
            (error as { error?: { data?: { message?: string } } })?.error?.data
              ?.message ?? "Could not start the export",
            { id: toastId, duration: 8000 },
          );
        }
      },
      // The list is the export tab's own view, so it has to refresh.
      invalidatesTags: [{ type: "TransferRuns", id: "LIST" }],
    }),

    /**
     * Poll target for one export. Like `getTransferRun`, it provides only its own
     * id so a 2-second poll cannot drag the whole history list with it.
     */
    getTransferExport: builder.query<
      IApiResponse<IRunWithView>,
      { runId: number }
    >({
      query: ({ runId }) => ({
        url: API_COMPANY_DATA_TRANSFER_EXPORT(runId),
        method: "GET",
      }),
      providesTags: (_r, _e, { runId }) => [{ type: "TransferRun", id: runId }],
    }),

    /** Raw rows for the drill-down drawer; the summary view uses grouped errors. */
    listTransferRowErrors: builder.query<
      IListRowErrorsResponse,
      IListRowErrorsParams
    >({
      query: ({ runId, ...params }) => ({
        url: API_COMPANY_DATA_TRANSFER_ERRORS(runId),
        method: "GET",
        params,
      }),
      providesTags: (_r, _e, { runId }) => [{ type: "TransferErrors", id: runId }],
    }),
  }),
});

export const {
  useGetTransferEntitiesQuery,
  useListTransferRunsQuery,
  useGetTransferRunQuery,
  useAnalyzeTransferWorkbookMutation,
  useUpdateTransferMappingMutation,
  useValidateTransferRunMutation,
  useRunTransferImportMutation,
  useCancelTransferRunMutation,
  useUndoTransferRunMutation,
  useListTransferRowErrorsQuery,
  useCreateTransferExportMutation,
  useGetTransferExportQuery,
} = companyDataTransferApi;
